import React from 'react';
import { useTranslation } from 'react-i18next';
import type { GameRTPComponent } from '../../../types/builder';
import { ColorPicker } from '../../Common/ColorPicker';
import { ImageUploader } from '../../Common/ImageUploader';
import { ToggleSwitch } from '../../Common/ToggleSwitch';
import { Plus, Trash2 } from 'lucide-react';

interface GameRTPSettingsProps {
  component: GameRTPComponent;
  onUpdate: (settings: GameRTPComponent['settings']) => void;
}

export const GameRTPSettings: React.FC<GameRTPSettingsProps> = ({ component, onUpdate }) => {
  const { t } = useTranslation('builder');
  const { settings } = component;

  const handleChange = (field: string, value: any) => {
    onUpdate({
      ...settings,
      [field]: value,
    });
  };

  const handleGameChange = (gameId: string, field: string, value: any) => {
    const updatedGames = settings.games.map((game) =>
      game.id === gameId ? { ...game, [field]: value } : game
    );
    handleChange('games', updatedGames);
  };

  const addGame = () => {
    const newGame = {
      id: `game-${Date.now()}`,
      name: t('settings.gameRtp.defaultItemName'),
      banner: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=300&h=150&fit=crop',
      rtp: 85.0,
      provider: t('settings.gameRtp.defaultCategory'),
    };
    handleChange('games', [...settings.games, newGame]);
  };

  const removeGame = (gameId: string) => {
    if (settings.games.length <= 1) {
      alert(t('settings.gameRtp.atLeastOne'));
      return;
    }
    handleChange('games', settings.games.filter((g) => g.id !== gameId));
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.title')}
        </label>
        <input
          type="text"
          value={settings.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            {t('settings.gameRtp.items')}
          </label>
          <button
            onClick={addGame}
            className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            <Plus size={16} />
            {t('settings.gameRtp.addItem')}
          </button>
        </div>

        <div className="space-y-4">
          {settings.games.map((game, index) => (
            <div key={game.id} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  {t('settings.gameRtp.itemNumber', { n: index + 1 })}
                </span>
                {settings.games.length > 1 && (
                  <button
                    onClick={() => removeGame(game.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    {t('settings.gameRtp.itemName')}
                  </label>
                  <input
                    type="text"
                    value={game.name}
                    onChange={(e) => handleGameChange(game.id, 'name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    {t('settings.gameRtp.category')}
                  </label>
                  <input
                    type="text"
                    value={game.provider}
                    onChange={(e) => handleGameChange(game.id, 'provider', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    {t('settings.gameRtp.progressPercent')}
                  </label>
                  <input
                    type="number"
                    value={game.rtp}
                    onChange={(e) => handleGameChange(game.id, 'rtp', parseFloat(e.target.value))}
                    min="0"
                    max="100"
                    step="0.1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    {t('settings.gameRtp.bannerImage')}
                  </label>
                  <ImageUploader
                    currentImage={game.banner}
                    onImageChange={(url) => handleGameChange(game.id, 'banner', url)}
                    aspectRatio="2:1"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <ToggleSwitch
          label={t('settings.gameRtp.showCategory')}
          checked={settings.showProvider}
          onChange={(checked) => handleChange('showProvider', checked)}
        />
      </div>

      <div>
        <ToggleSwitch
          label={t('settings.gameRtp.animateProgress')}
          checked={settings.animateRTP}
          onChange={(checked) => handleChange('animateRTP', checked)}
        />
        <p className="text-xs text-gray-500 mt-1">
          {t('settings.gameRtp.animateProgressHint')}
        </p>
      </div>

      {settings.animateRTP && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('settings.gameRtp.progressFluctuation')}
          </label>
          <input
            type="range"
            min="0.1"
            max="2"
            step="0.1"
            value={settings.rtpFluctuation}
            onChange={(e) => handleChange('rtpFluctuation', parseFloat(e.target.value))}
            className="w-full"
          />
          <div className="text-xs text-gray-500 mt-1">
            {t('settings.gameRtp.progressFluctuationDisplay', { value: settings.rtpFluctuation })}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.backgroundColor')}
        </label>
        <ColorPicker
          color={settings.backgroundColor}
          onChange={(color) => handleChange('backgroundColor', color)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.textColor')}
        </label>
        <ColorPicker
          color={settings.textColor}
          onChange={(color) => handleChange('textColor', color)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.gameRtp.progressBarColor')}
        </label>
        <ColorPicker
          color={settings.progressColor}
          onChange={(color) => handleChange('progressColor', color)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.gameRtp.progressBackgroundColor')}
        </label>
        <ColorPicker
          color={settings.progressBackgroundColor}
          onChange={(color) => handleChange('progressBackgroundColor', color)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.padding')}
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={settings.padding}
          onChange={(e) => handleChange('padding', parseInt(e.target.value))}
          className="w-full"
        />
        <div className="text-sm text-gray-600 text-center mt-1">{settings.padding}px</div>
      </div>
    </div>
  );
};
